import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import LifecycleChecklist from "./LifecycleChecklist";
import { SignOut, Trash, CaretDown, CaretUp } from "@phosphor-icons/react";

const STATUS_STYLE = {
  requested: "bg-amber-50 text-amber-700 border-amber-200",
  in_review: "bg-sky-50 text-sky-700 border-sky-200",
  completed: "bg-muted text-muted-foreground border-border",
  cancelled: "bg-muted text-muted-foreground border-border",
};

export default function ConsoleOffboarding() {
  const [requests, setRequests] = useState([]);
  const [log, setLog] = useState([]);
  const [expanded, setExpanded] = useState("");

  const load = useCallback(() => {
    api.get("/console/offboarding/requests").then((r) => setRequests(r.data.requests || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/console/offboarding/users").then((r) => setLog(r.data.entries || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (req) => {
    if (!window.confirm(`FINAL STEP — permanently remove "${req.entity_name}" and purge all its data? This cannot be undone.`)) return;
    try {
      const r = await api.post(`/console/offboarding/requests/${req.id}/complete`);
      toast.success(`Entity closed. Purged ${Object.values(r.data.purged || {}).reduce((a, b) => a + b, 0)} records.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const visible = requests.filter((r) => r.status !== "cancelled");

  return (
    <div className="space-y-8" data-testid="console-offboarding">
      <div className="border border-border bg-card">
        <div className="border-b border-border px-5 py-3 flex items-center gap-2">
          <SignOut size={18} weight="duotone" />
          <span className="overline text-muted-foreground">Offboarding requests</span>
        </div>
        {requests.length === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground" data-testid="console-offboarding-empty">No offboarding requests.</div>
        ) : visible.length === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground" data-testid="console-offboarding-empty">No active or completed offboarding requests.</div>
        ) : (
          <div className="divide-y divide-border">
            {visible.map((req) => (
              <div key={req.id} data-testid={`offboarding-req-${req.id}`}>
                <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-foreground">{req.entity_name}</div>
                    <div className="text-[12px] text-muted-foreground">
                      {req.reason}{req.detail ? ` — ${req.detail}` : ""} · requested by {req.requested_by} · {new Date(req.requested_at).toLocaleDateString()}
                      {req.contact_requested && <span className="ml-1 text-sky-700 font-semibold">· asked for contact</span>}
                    </div>
                  </div>
                  <span className={`text-[11px] font-semibold border px-2 py-0.5 ${STATUS_STYLE[req.status] || ""}`}>{req.status}</span>
                  {["requested", "in_review"].includes(req.status) && (
                    <>
                      <Button size="sm" variant="outline" className="rounded-none"
                        data-testid={`offboarding-review-${req.id}`}
                        onClick={() => setExpanded(expanded === req.id ? "" : req.id)}>
                        Review checklist {expanded === req.id ? <CaretUp size={13} className="ml-1" /> : <CaretDown size={13} className="ml-1" />}
                      </Button>
                      <Button size="sm" className="rounded-none bg-red-600 hover:bg-red-700"
                        data-testid={`offboarding-complete-${req.id}`} onClick={() => complete(req)}>
                        <Trash size={14} className="mr-1" /> End service &amp; close
                      </Button>
                    </>
                  )}
                </div>
                {expanded === req.id && (
                  <div className="px-5 pb-5 bg-secondary/20">
                    <LifecycleChecklist type="offboarding" rid={req.registration_id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <span className="overline text-muted-foreground">User removal log (all entities)</span>
        </div>
        {log.length === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground">No user removals recorded.</div>
        ) : (
          <table className="w-full text-sm" data-testid="console-removal-log">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="px-5 py-2">User</th><th className="px-3 py-2">Entity</th><th className="px-3 py-2">Left</th><th className="px-3 py-2">By</th><th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {log.map((e) => (
                <tr key={e.id} className="border-b border-border">
                  <td className="px-5 py-2"><div className="font-medium text-foreground">{e.name || e.email}</div><div className="text-[12px] text-muted-foreground">{e.email}</div></td>
                  <td className="px-3 py-2 text-[13px]">{e.entity_name}</td>
                  <td className="px-3 py-2 text-[12px] text-muted-foreground font-mono">{new Date(e.removed_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-[12px] text-muted-foreground">{e.removed_by}</td>
                  <td className="px-3 py-2 text-[12px]">{e.reactivated ? <span className="text-emerald-700 font-semibold">Reactivated</span> : e.user_deleted ? "Deleted" : "Removed"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
